/**
 * Utility for sanitizing HTML content to prevent XSS attacks
 * Uses a whitelist approach for allowed tags and attributes
 */

// List of allowed HTML tags for basic formatting
const ALLOWED_TAGS = new Set([
	'b', 'i', 'em', 'strong', 'a', 'br', 'p', 'span', 'div',
	'ul', 'ol', 'li', 'code', 'pre', 'blockquote'
])

// List of allowed attributes per tag
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
	a: new Set(['href', 'title', 'target']),
	span: new Set(['class', 'style']),
	div: new Set(['class', 'style'])
}

// List of allowed URL schemes
const ALLOWED_SCHEMES = new Set(['http', 'https', 'mailto', 'tel'])

/**
 * Sanitizes HTML content by removing potentially dangerous elements and attributes
 * @param html - The HTML string to sanitize
 * @param options - Optional configuration for sanitization
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(
	html: string,
	options: {
		allowedTags?: string[]
		allowedAttributes?: Record<string, string[]>
		allowStyles?: boolean
	} = {}
): string {
	// Create a temporary container
	const temp = document.createElement('div')

	temp.innerHTML = html

	// Merge custom allowed tags with defaults
	const allowedTags = options.allowedTags
		? new Set([...ALLOWED_TAGS, ...options.allowedTags])
		: ALLOWED_TAGS

	// Merge custom allowed attributes with defaults
	const allowedAttributes = { ...ALLOWED_ATTRIBUTES }
	if (options.allowedAttributes) {
		for (const [tag, attrs] of Object.entries(options.allowedAttributes)) {
			allowedAttributes[tag] = new Set([
				...(allowedAttributes[tag] || []),
				...attrs
			])
		}
	}

	// Recursively clean nodes
	function cleanNode(node: Node): Node | null {
		if (node.nodeType === Node.TEXT_NODE) {
			return node
		}

		if (node.nodeType !== Node.ELEMENT_NODE) {
			return null
		}

		const element = node as Element
		const tagName = element.tagName.toLowerCase()

		// Remove disallowed tags but keep their text content
		if (!allowedTags.has(tagName)) {
			// For disallowed tags, only return their text content
			if (element.childNodes.length === 1 && element.childNodes[0]?.nodeType === Node.TEXT_NODE) {
				return element.childNodes[0]
			}

			// For complex content, recursively clean children
			const fragment = document.createDocumentFragment()

			for (const child of Array.from(element.childNodes)) {
				const cleanChild = cleanNode(child)
				if (cleanChild) {
					fragment.appendChild(cleanChild)
				}
			}
			return fragment.hasChildNodes() ? fragment : null
		}

		// Create a clean element
		const cleanElement = document.createElement(tagName)

		// Clean attributes
		const allowedAttrs = allowedAttributes[tagName] || new Set()

		for (const attr of Array.from(element.attributes)) {
			if (allowedAttrs.has(attr.name)) {
				// Special handling for href to prevent javascript: URLs
				if (attr.name === 'href' || attr.name === 'src') {
					const url = attr.value.trim()

					try {
						const parsed = new URL(url, window.location.href)
						if (ALLOWED_SCHEMES.has(parsed.protocol.slice(0, -1))) {
							cleanElement.setAttribute(attr.name, attr.value)
						}
					} catch {
						// Invalid URL, skip it
					}
				} else if (attr.name === 'style' && !options.allowStyles) {
					// Skip style attributes unless explicitly allowed
					continue
				} else {
					cleanElement.setAttribute(attr.name, attr.value)
				}
			}
		}

		// Recursively clean child nodes
		for (const child of Array.from(element.childNodes)) {
			const cleanChild = cleanNode(child)
			if (cleanChild) {
				cleanElement.appendChild(cleanChild)
			}
		}

		return cleanElement
	}

	// Clean all child nodes
	const fragment = document.createDocumentFragment()

	for (const child of Array.from(temp.childNodes)) {
		const cleanChild = cleanNode(child)
		if (cleanChild) {
			fragment.appendChild(cleanChild)
		}
	}

	// Convert back to HTML string
	const container = document.createElement('div')

	container.appendChild(fragment)
	return container.innerHTML
}

/**
 * Escapes HTML special characters to prevent XSS
 * Use this for displaying user input as plain text
 */
export function escapeHtml(str: string): string {
	const div = document.createElement('div')

	div.textContent = str
	return div.innerHTML
}

/**
 * Sanitizes user input for safe display
 * Allows basic formatting tags but removes scripts and event handlers
 */
export function sanitizeUserInput(input: string): string {
	return sanitizeHtml(input, {
		allowedTags: ['b', 'i', 'em', 'strong', 'br', 'p'],
		allowStyles: false
	})
}

/**
 * Sanitizes rich text content with more allowed formatting
 */
export function sanitizeRichText(input: string): string {
	return sanitizeHtml(input, {
		allowedTags: ['b', 'i', 'em', 'strong', 'a', 'br', 'p', 'span', 'div', 'ul', 'ol', 'li'],
		allowStyles: true
	})
}
